// @flow

// Types
import type { Asset } from 'fc-model-assets/types'
import type { Point } from 'fc-model-sampling-points/types'

// Libs
import React, { PureComponent } from 'react'
import _get from 'lodash/get'
import _isEmpty from 'lodash/isEmpty'
import _find from 'lodash/find'

// Components
import Select from 'react-select'
import FormField from '@react-mdc/form-field'
import Checkbox from '@react-mdc/checkbox'
import JobDetail from '../containers/JobDetailContainer'
import { AddJobButton, SubmitCancel } from 'hfty-components-soil-job'
import { Errors } from 'hfty-components-errors'
import PanelAssetsForm from './PanelAssetsForm'
import AddJobForm from './jobs-panel/AddJobForm'
import messages from '../messages'
import DownloadIcon from 'fc-icons/src/Download'

// Utils
import getUrl from 'hfty-utils/url'

// Constants
import { PROP_STATUS, RESULTS_POSTED } from 'hfty-model-soil-jobs'
import { SOIL_PROPS_PDF_NOT_GENERATED } from 'hfty-map-dom'

// Styles
import { flex } from 'hfty-styles/src/mixins'
import { INDENT, message as messageCommonStyle } from 'hfty-styles/src/common'
import { white, primary } from 'hfty-styles/src/colors'

const styles = {
  bottom: {
    display: 'flex',
    marginTop: 'auto',
    flexDirection: 'column',
    paddingLeft: 5,
  },
  selectors: {
    marginBottom: INDENT,
  },
  jobsPanel: {
    padding: `0 ${INDENT}px`,
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
  },
  addBtnWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: `${INDENT}px 0 30px`,
  },
  downloadBtnWrapper: {
    ...flex('space-between', 'center'),
    fontSize: 15,
    marginTop: INDENT,
  },
  downloadIcon: {
    verticalAlign: 'middle',
    zIndex: -1,
  },
  btnsHolder: {
    marginBottom: 30,
    textAlign: 'right',
    backgroundColor: primary(),
    color: white(),
  },
  message: {
    marginTop: 9,
  },
  checkboxWrapper: {
    display: 'flex',
    marginTop: 15,
    fontSize: '.875rem',
    alignItems: 'center',
  },
  checkbox: {
    position: 'absolute',
    margin: '0 4px 0 -11px',
  },
  label: {
    paddingLeft: '32px',
  },
}

export type Props = {
  fields: Array<Asset>,
  selectedSeasonId: ?number,
  selectedFarmId: ?number,
  selectedFieldId: ?number,
  subfield: ?Object,
  selectedJob: ?Object,
  // selectedJobDetail: ?Object,
  userId: ?number,
  seasonSelectorOptions: Array<{ id: number, value: string }>,
  farmSelectorOptions: Array<{ id: number, value: string }>,
  fieldSelectorOptions: Array<{ id: number, value: string }>,
  jobSelectorOptions: Array<{ id: number, value: string }>,
  assigneeOptions: Array<{ id: number, value: string }>,
  operations: Array<Object>,
  operation: Object,
  area: ?number,
  gridData: Object,
  selectedFeatureIndices: Array<number>,
  token: string,
  hoverFeatureIndex: number,
  selectedFeatureIndex: number,
  featuresOfSoilJob: Array<Object>,
  match: Object,
  soilPropsMapBlobId: number,
  previousPoints: Array<Point>,
  isPreviousPointsShown: boolean,
  actions: {
    onChangeSeason: Function,
    onChangeFarm: Function,
    onChangeField: Function,
    onSelectJob: Function,
    onAddJob: Function,
    onSubmitJob: Function,
    onCancelJob: Function,
    onUpdateJob: Function,
    onSelectFeature: Function,
    onToggleHoverFeature: Function,
    preselectedJob: Function,
    clearMapUrlFromJobId: Function,
    showPreviousPoints: Function,
    centerMapToFarm: Function,
    centerMapToField: Function,
  },
}

// Local Functions
function onChange(cb: Function): Function {
  return function (option: ?{ value: number }) {
    if (option) {
      cb(option.value || null)
      return
    }

    cb(null)
  }
}

// Exports
export class JobsPanel extends PureComponent {
  props: Props
  onSubmit: Function
  onCancel: Function
  onAddJob: Function
  onSelectJob: Function

  static defaultProps = {
    previousPoints: [],
    jobSelectorOptions: [],
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      init: false,
    }

    this.onChangePrevPointCheckbox = this.onChangePrevPointCheckbox.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onCancel = this.onCancel.bind(this)
    this.onAddJob = this.onAddJob.bind(this)
    this.onSelectJob = this.onSelectJob.bind(this)
    this.changeHandlerWrapper = this.changeHandlerWrapper.bind(this)
  }

  componentDidMount() {
    const jobId = _get(this.props.match, 'params.jobId', undefined)
    const selectedJobId = _get(this.props, 'selectedJob.id', undefined)
    const isNewPreselectedJob = jobId && (jobId !== selectedJobId || !this.props.selectedJob)

    if (isNewPreselectedJob) {
      this.props.actions.preselectedJob(+jobId)
    }
  }

  onChangePrevPointCheckbox(e: Event) {
    this.props.actions.showPreviousPoints(e.target.checked)
  }

  onSubmit() {
    this.props.actions.onSubmitJob(this.props.operations)
    this.props.actions.onSelectFeature([])
  }

  onCancel() {
    this.props.actions.onCancelJob(this.props.operations)
    this.props.actions.onSelectFeature([])
  }

  onAddJob() {
    this.props.actions.onAddJob(this.props.userId, this.props.subfield.id)
    this.props.actions.onSelectFeature([])
  }

  onSelectJob(option: Object): * {
    const jobId = option ? option.value : null

    onChange(this.props.actions.onSelectJob(jobId))
  }

  renderDownloadButton(): React.element<any> {
    const isDisabled = !this.props.soilPropsMapBlobId || this.props.soilPropsMapBlobId === SOIL_PROPS_PDF_NOT_GENERATED
    const linkAttrs = {}

    if (!isDisabled) {
      linkAttrs.href = getUrl({
        path: `blobstore/${this.props.soilPropsMapBlobId}`,
        excludeTrailingSlash: true,
        queryParams: {
          download: true,
        },
        token: this.props.token,
      })
    }

    return (
      <div>
        <div style={styles.downloadBtnWrapper}>
          Download map with job details
          <a {...linkAttrs} className="button-link">
            <DownloadIcon size={24} color={white()} />
          </a>
        </div>
        {this.props.soilPropsMapBlobId === SOIL_PROPS_PDF_NOT_GENERATED && (
          <p style={{ ...messageCommonStyle, ...styles.message }}>This will be disabled until the results are posted</p>
        )}
      </div>
    )
  }

  renderPanelFooter(): React.element<any> {
    const { selectedFieldId, subfield, jobSelectorOptions, fields } = this.props
    // The field has a job, can only have one job per field, no need to add
    if (!_isEmpty(jobSelectorOptions)) {
      return (
        <div style={styles.addBtnWrapper}>
          <AddJobButton disabled />
        </div>
      )
    }

    if (selectedFieldId && fields) {
      const selectedField = _find(fields, (field: Object): boolean => selectedFieldId === field.id)
      if (selectedField && !selectedField.shape) {
        return 'Cannot add a job to a field with no field border'
      }
    }
    return selectedFieldId ? (
      subfield ? (
        <div style={styles.addBtnWrapper}>
          <AddJobButton onClick={this.onAddJob} />
        </div>
      ) : (
        'You can only create a job for a subfield'
      )
    ) : (
      <div style={{ padding: `${INDENT}px 0` }}>Please select a field</div>
    )
  }

  changeHandlerWrapper(handler: Function): Function {
    return (value: number | string): Function => {
      this.props.actions.clearMapUrlFromJobId()
      handler(value)
    }
  }

  render(): React.element<any> {
    const isResultsPostedStatus = _get(this.props, `selectedJob[${PROP_STATUS}]`) === RESULTS_POSTED
    const shouldRenderPrevPointsCheckbox = this.props.previousPoints && this.props.previousPoints.length > 0

    return (
      <div style={styles.jobsPanel}>
        {_isEmpty(this.props.operations) ? (
          <div style={styles.formContainer}>
            <div style={styles.selectors} className="select-group-aligned-right w80">
              <PanelAssetsForm
                selectedSeasonId={this.props.selectedSeasonId}
                seasonSelectorOptions={this.props.seasonSelectorOptions}
                onChangeSeason={this.changeHandlerWrapper(this.props.actions.onChangeSeason)}
                selectedFarmId={this.props.selectedFarmId}
                farmSelectorOptions={this.props.farmSelectorOptions}
                onChangeFarm={this.changeHandlerWrapper(this.props.actions.onChangeFarm)}
                selectedFieldId={this.props.selectedFieldId}
                fieldSelectorOptions={this.props.fieldSelectorOptions}
                onChangeField={this.changeHandlerWrapper(this.props.actions.onChangeField)}
                fields={this.props.fields}
                centerMapToField={this.props.actions.centerMapToField}
                centerMapToFarm={this.props.actions.centerMapToFarm}
              >
                <FormField>
                  <label htmlFor="job-selector">Job</label>
                  <Select
                    name="job-selector"
                    placeholder={'Jobs'}
                    value={this.props.selectedJob ? this.props.selectedJob.id : null}
                    disabled={!this.props.jobSelectorOptions.length}
                    options={this.props.jobSelectorOptions}
                    onChange={(op: ?Object): * => this.onSelectJob(op)}
                    className="clear-margin"
                  />
                </FormField>
              </PanelAssetsForm>
            </div>
            {isResultsPostedStatus && this.renderDownloadButton()}
            {shouldRenderPrevPointsCheckbox && (
              <div style={styles.checkboxWrapper}>
                <Checkbox.Default
                  id="job-show-prev-points"
                  name="job-show-prev-points"
                  checked={this.props.isPreviousPointsShown}
                  onChange={this.onChangePrevPointCheckbox}
                  style={styles.checkbox}
                  className="thin-border"
                />
                <label htmlFor="show-prev-points" style={styles.label}>
                  {`View Previous Season's Sampling Points`}
                </label>
              </div>
            )}
            {this.props.selectedJob ? (
              <JobDetail selectedJob={this.props.selectedJob} gridData={this.props.gridData} token={this.props.token} />
            ) : null}
            {this.renderPanelFooter()}
          </div>
        ) : (
          <div style={styles.formContainer}>
            <AddJobForm
              usersSelectorOptions={this.props.assigneeOptions}
              statusOptions={[]}
              samplingTypeOptions={[]}
              onUpdateJob={this.props.actions.onUpdateJob}
              operation={this.props.operation}
              selectedFieldId={this.props.selectedFieldId}
              fieldSelectorOptions={this.props.fieldSelectorOptions}
              area={this.props.area}
              gridData={this.props.gridData}
              selectedFeatureIndices={this.props.selectedFeatureIndices}
              featuresOfSoilJob={this.props.featuresOfSoilJob}
              onSelectFeature={this.props.actions.onSelectFeature}
              onToggleHoverFeature={this.props.actions.onToggleHoverFeature}
              selectedFeatureIndex={this.props.selectedFeatureIndex}
              hoverFeatureIndex={this.props.hoverFeatureIndex}
              previousPoints={this.props.previousPoints}
              isPreviousPointsShown={this.props.isPreviousPointsShown}
              showPreviousPoints={this.props.actions.showPreviousPoints}
            />
            <div style={styles.bottom}>
              {this.props.operation ? <Errors errors={this.props.operation.errors} messages={messages} /> : null}
              <div>
                <SubmitCancel onClickSubmit={this.onSubmit} onClickCancel={this.onCancel} style={styles.btnsHolder} />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }
}

export default JobsPanel
